import React, { useState } from 'react';
import {
  X,
  Printer,
  Share2,
  Download,
  MessageSquare,
  FileText,
  Check,
  ChevronDown,
  Sparkles
} from 'lucide-react';
import { MerchantInvoice, MerchantBillingSettings, InvoiceTemplateId } from '../../types/gst';
import { InvoiceTemplateRenderer } from './InvoiceTemplateRenderer';
import { INVOICE_TEMPLATES, generateInvoiceWhatsAppLink } from '../../services/billingService';
import { toast } from '../ui';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  invoice: MerchantInvoice | null;
  settings?: MerchantBillingSettings;
  onUpdateTemplate?: (templateId: InvoiceTemplateId) => Promise<void>;
}

export const InvoiceViewerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  invoice,
  settings,
  onUpdateTemplate
}) => {
  if (!isOpen || !invoice) return null;

  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplateId>(
    invoice.templateId || 'CLASSIC_GST'
  );
  const [isChanging, setIsChanging] = useState<boolean>(false);

  const handlePrint = () => {
    window.print();
  };

  const handleWhatsApp = () => {
    const link = generateInvoiceWhatsAppLink(invoice, settings);
    window.open(link, '_blank');
    toast.success(`WhatsApp dispatch opened for invoice #${invoice.invoiceNumber}`);
  };

  const handleTemplateChange = async (templateId: InvoiceTemplateId) => {
    setSelectedTemplate(templateId);
    if (onUpdateTemplate) {
      setIsChanging(true);
      try {
        await onUpdateTemplate(templateId);
        toast.success(`Default template changed to ${templateId}`);
      } catch (err: any) {
        toast.error('Failed to update template: ' + (err?.message || ''));
      } finally {
        setIsChanging(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto print:p-0 print:bg-white print:fixed-none">
      <div className="relative w-full max-w-5xl rounded-3xl border border-white/10 bg-[#12141a] shadow-2xl flex flex-col max-h-[92vh] overflow-hidden print:border-none print:shadow-none print:max-h-none print:bg-white">
        {/* Top Control Bar (Hidden when printing) */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-white/10 bg-[#0e1015] print:hidden">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-cyan-400" />
              <div>
                <h3 className="font-bold text-white text-sm">Invoice #{invoice.invoiceNumber}</h3>
                <p className="text-[11px] text-slate-400">
                  {invoice.customerName} &bull; ₹{invoice.grandTotal.toLocaleString('en-IN')}
                </p>
              </div>
            </div>

            {/* Template Selector Pill */}
            <div className="flex items-center gap-1.5 pl-4 border-l border-white/10">
              <span className="text-[10px] font-bold uppercase text-slate-400">Template:</span>
              <div className="flex gap-1 overflow-x-auto max-w-sm py-0.5">
                {INVOICE_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => handleTemplateChange(t.id)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition ${
                      selectedTemplate === t.id
                        ? 'bg-cyan-500 text-black font-bold shadow-sm shadow-cyan-500/25'
                        : 'bg-white/5 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* 1-Click WhatsApp Button */}
            <button
              onClick={handleWhatsApp}
              className="flex items-center gap-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-3 py-1.5 text-xs font-semibold border border-emerald-500/30 transition shadow-sm"
              title="Share pre-filled invoice message on WhatsApp"
            >
              <MessageSquare className="h-3.5 w-3.5 text-emerald-400" />
              <span>WhatsApp</span>
            </button>

            {/* Print / Save PDF Button */}
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 text-xs font-semibold transition"
            >
              <Printer className="h-3.5 w-3.5 text-slate-300" />
              <span>Print / PDF</span>
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition ml-2"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Invoice Body Container */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-900/60 print:p-0 print:bg-white">
          <InvoiceTemplateRenderer
            invoice={invoice}
            settings={settings}
            overrideTemplateId={selectedTemplate}
          />
        </div>
      </div>
    </div>
  );
};
